import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, MessageSquareText, Search, Sparkles, Users } from "lucide-react";
import { Reveal } from "@/components/marketing/kit";

const STEPS = [
  { n: "01", icon: <Search />, t: "Discover", p: "Describe your campaign and let AI surface the best-fit TikTok & Instagram creators, ranked by audience, engagement and safety.", img: "/shots/discover.jpg", alt: "HypeFlow creator discovery with AI-ranked TikTok and Instagram creators", rev: false, cap: "Live creator discovery — search any @handle or discover new creators by keyword, location and audience size." },
  { n: "02", icon: <Users />, t: "Connect", p: "Shortlist creators into your CRM pipeline and move each relationship from Prospect to Partner — with real audience numbers attached.", img: "/shots/connect.jpg", alt: "HypeFlow creator CRM relationship pipeline", rev: true, cap: "Your private creator CRM — every relationship stage, audience size and platform in one place." },
  { n: "03", icon: <MessageSquareText />, t: "Create", p: "Spin up a campaign workspace, attach creators from your CRM, and watch reach, engagement and EMV auto-compute from real stats.", img: "/shots/campaign.jpg", alt: "HypeFlow campaign workspace with auto-computed reach, EMV and attached creators", rev: false, cap: "Campaign workspace — attach creators and metrics (reach, EMV, spend, progress) recalculate instantly." },
  { n: "04", icon: <BarChart3 />, t: "Prove", p: "Track reach, engagement, EMV and ROI live — computed from your real campaigns — then let AI turn results into your next best move.", img: "/shots/analytics.jpg", alt: "HypeFlow performance analytics computed from real campaigns", rev: true, cap: "Performance analytics — trends and channel mix computed from your real campaign data, not demo numbers." },
];

export default function HowItWorks() {
  return (
    <main>
      <section className="mk-sec" style={{ paddingBottom: 20 }}><div className="mk-wrap mk-head">
        <Reveal><span className="mk-pill"><Sparkles size={14} /> How it works</span></Reveal>
        <Reveal delay={0.08}><h1 className="mk-h2" style={{ fontSize: "clamp(34px,5vw,58px)" }} data-testid="how-title">From idea to viral campaign in four moves</h1></Reveal>
        <Reveal delay={0.16}><p data-testid="how-description">No more spreadsheets, scattered DMs or guesswork. One connected flow — Discover, Connect, Create, Prove. Here's exactly how it looks inside the app today.</p></Reveal>
      </div></section>

      <section className="mk-sec" style={{ paddingTop: 0 }}><div className="mk-wrap">
        <div className="mk-steps">{STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}><div className="mk-step" data-testid={`how-step-${s.n}`}>
            <span className="num">{s.n}</span><span className="ic">{s.icon}</span><h3>{s.t}</h3><p>{s.p}</p>
          </div></Reveal>
        ))}</div>

        <Reveal delay={0.1}><div className="mk-tour-hero" data-testid="how-tour-hero">
          <div className="mk-shot" data-testid="how-shot-overview">
            <div className="mk-shot-bar"><i /><i /><i /></div>
            <img src="/shots/overview.jpg" alt="HypeFlow command center dashboard with live campaign momentum and creator radar" loading="lazy" />
          </div>
          <p style={{ textAlign: "center", color: "var(--tx2)", fontSize: 14, marginTop: 18 }}>Your command center — active campaigns, portfolio reach, earned media value and what needs attention, at a glance.</p>
        </div></Reveal>
      </div></section>

      <section className="mk-sec" style={{ paddingTop: 0 }}><div className="mk-wrap">
        <div className="mk-tour" data-testid="how-product-tour">{STEPS.map((s) => (
          <Reveal key={s.n}><div className={`mk-tour-row ${s.rev ? "rev" : ""}`} data-testid={`how-tour-row-${s.n}`}>
            <div className="mk-tour-copy">
              <span className="mk-tour-ic">{s.icon}</span>
              <span className="num">STEP {s.n}</span>
              <h3>{s.t}</h3>
              <p>{s.p}</p>
            </div>
            <div className="mk-shot" data-testid={`how-tour-shot-${s.n}`}>
              <div className="mk-shot-bar"><i /><i /><i /></div>
              <img src={s.img} alt={s.alt} loading="lazy" />
            </div>
          </div></Reveal>
        ))}</div>
      </div></section>

      <section className="mk-sec"><div className="mk-wrap"><div className="mk-cta">
        <span className="mk-pill" style={{ margin: "0 auto" }}><Sparkles size={14} /> Ready in 5 minutes</span>
        <h2>Run your first campaign this week</h2>
        <p>Start free and go from brief to shortlisted creators before your coffee gets cold.</p>
        <div className="mk-cta-actions"><Link to="/login?mode=register" className="mk-btn primary lg" data-testid="how-final-trial">Start free <ArrowRight size={17} /></Link><Link to="/pricing" className="mk-btn ghost lg">See pricing</Link></div>
      </div></div></section>
    </main>
  );
}
